// 资源包发布页 (/knowledge/releases)。运营在 admin 改完内容(脚本/音频/媒体)
// 后,在这里点"发布新版本" → 服务端凝固当前 manifest 快照成一行 pack_releases,
// 给 mobile 端做"版本化推送"的源头。中间反复改 admin 不会打扰用户,只有"发布"
// 这个动作才会让 300 台手机看到新版本提示。
import { useEffect, useState } from "react";
import { apiGet, apiSend } from "@/lib/api";
import { confirmDialog, toast } from "@/components/ui/feedback";

interface PackRelease {
  id: string;
  versionCode: number;
  versionName: string;
  changelog: string;
  mandatory: boolean;
  totalCount: number;
  totalBytes: number;
  createdAt: string;
}

function fmtBytes(n: number): string {
  if (n <= 0) return "0";
  const mb = n / 1024 / 1024;
  if (mb < 1024) return `${mb.toFixed(mb < 10 ? 1 : 0)} MB`;
  return `${(mb / 1024).toFixed(2)} GB`;
}

// 解析上一版 versionName 里的主/次版本号(格式 V<major>.<minor>,大小写都吃),
// 兼容老 "vN"(用作 major)/纯数字。次版本 +1 作为下一版默认;主版本要改就手动
// 编辑输入框。首次发布默认 V1.0。
function nextVersionName(prev?: string): string {
  const m = prev ? /^v(\d+)(?:\.(\d+))?$/i.exec(prev.trim()) : null;
  if (!m) return "V1.0";
  const major = parseInt(m[1], 10);
  const minor = m[2] != null ? parseInt(m[2], 10) : 0;
  return `V${major}.${minor + 1}`;
}

interface SceneChange {
  sceneId: string;
  name: string;
  updatedAt: string;
}
interface AudioIssue {
  sceneId: string;
  sceneName: string;
  part: string;
  variant: string;
  voice: string;
  lang: string;
  reason: string; // stale | pending | failed
  detail: string;
}
interface PhraseChange {
  id: string;
  name: string;
  updatedAt: string;
}
interface ReleasePreview {
  lastReleasedAt: string;
  lastReleaseVersion: string;
  newScenes: SceneChange[];
  scriptChangedScenes: SceneChange[];
  mediaChangedScenes: SceneChange[];
  newImagesCount: number;
  newImagesScenes: SceneChange[];
  presetChangedPhrases: PhraseChange[];
  staleAudios: AudioIssue[];
  pendingAudios: AudioIssue[];
  failedAudios: AudioIssue[];
  clean: boolean;
}

function PublishModal({ open, onClose, onPublished, nextCodeHint, defaultVersionName }: {
  open: boolean;
  onClose: () => void;
  onPublished: () => void;
  nextCodeHint: number;
  defaultVersionName: string;
}) {
  const [versionName, setVersionName] = useState(defaultVersionName);
  const [mandatory, setMandatory] = useState(false);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<ReleasePreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setVersionName(defaultVersionName);
      setMandatory(false);
      setBusy(false);
      setPreview(null);
      // 打开弹窗即拉预览:发布前把 script 变动 / stale / pending / failed 一次列全
      setPreviewLoading(true);
      apiGet<ReleasePreview>("/admin/pack-releases/preview")
        .then((p) => setPreview(p))
        .catch((e) => toast(e instanceof Error ? e.message : "预览加载失败", "error"))
        .finally(() => setPreviewLoading(false));
    }
  }, [open, defaultVersionName]);

  if (!open) return null;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const finalName = versionName.trim() || defaultVersionName;
    // 有 stale/pending/failed 时给运营明显警告(不阻断,让他自己判断)
    const issueCount = preview
      ? preview.staleAudios.length + preview.pendingAudios.length + preview.failedAudios.length
      : 0;
    const warn = issueCount > 0
      ? `\n\n⚠️ 有 ${preview!.staleAudios.length} 条讲解词改了但音频未重跑、${preview!.pendingAudios.length} 条正在生成、${preview!.failedAudios.length} 条失败。这些音轨不会进入本次发布,建议先修复完再发。`
      : "";
    if (!(await confirmDialog({
      title: "确认发布新版资源包?",
      message: `版本号: ${finalName}\n强制更新: ${mandatory ? "是" : "否"}\n\n发布后,300 台已装设备会在 App 检测时看到新版本提示。App 更新弹窗里的"更新内容"由服务端根据本次预览自动生成。${warn}`,
      confirmText: issueCount > 0 ? "仍然发布" : "发布",
      danger: issueCount > 0,
    }))) return;
    setBusy(true);
    try {
      await apiSend("POST", "/admin/pack-releases", {
        versionName: finalName,
        mandatory,
      });
      toast("发布成功", "success");
      onPublished();
      onClose();
    } catch (e) {
      toast(e instanceof Error ? e.message : "发布失败", "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6 space-y-3" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-bold text-gray-900">发布新版资源包</h3>
        <p className="text-xs text-gray-500">
          版本名默认 <strong>{defaultVersionName}</strong>(次版本 +1),要升大版本请手动改。发布后 App 更新弹窗里的"更新内容"由服务端根据下方预览自动生成,无需手填。
        </p>

        {/* 发布预览: 上次发布以来的变更清单 + 卡点音轨 */}
        <div className="border border-gray-200 rounded-lg p-3 bg-gray-50 space-y-2 text-xs">
          <div className="flex items-center justify-between">
            <div className="font-semibold text-gray-700">
              <i className="ri-file-list-3-line mr-1" />发布内容预览
              {preview?.lastReleaseVersion && (
                <span className="ml-2 font-normal text-gray-500">
                  (对比上次 {preview.lastReleaseVersion} · {preview.lastReleasedAt})
                </span>
              )}
            </div>
            {preview && (
              <span className={`px-2 py-0.5 rounded font-semibold ${preview.clean ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                {preview.clean ? "无卡点" : `${preview.staleAudios.length + preview.pendingAudios.length + preview.failedAudios.length} 条卡点`}
              </span>
            )}
          </div>

          {previewLoading && <div className="text-gray-400">加载中…</div>}

          {preview && !previewLoading && (
            <div className="space-y-2">
              {/* 上次发布以来的内容变更 */}
              <div className="grid grid-cols-5 gap-2">
                <div className="bg-white rounded p-2 border border-gray-100">
                  <div className="text-gray-500">新增点位</div>
                  <div className="text-base font-semibold text-gray-900">{preview.newScenes.length}</div>
                </div>
                <div className="bg-white rounded p-2 border border-gray-100">
                  <div className="text-gray-500">讲解词变更</div>
                  <div className="text-base font-semibold text-gray-900">{preview.scriptChangedScenes.length}</div>
                </div>
                <div className="bg-white rounded p-2 border border-gray-100">
                  <div className="text-gray-500">点位/媒体变更</div>
                  <div className="text-base font-semibold text-gray-900">{preview.mediaChangedScenes.length}</div>
                </div>
                <div className="bg-white rounded p-2 border border-gray-100">
                  <div className="text-gray-500">图片新增</div>
                  <div className="text-base font-semibold text-gray-900">{preview.newImagesCount}</div>
                </div>
                <div className="bg-white rounded p-2 border border-gray-100">
                  <div className="text-gray-500">预设话术</div>
                  <div className="text-base font-semibold text-gray-900">{preview.presetChangedPhrases.length}</div>
                </div>
              </div>

              {/* stale 音轨警告 */}
              {preview.staleAudios.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded p-2">
                  <div className="font-semibold text-red-700 flex items-center gap-1">
                    <i className="ri-error-warning-line" />
                    {preview.staleAudios.length} 条讲解词改了但音频未重跑 (stale)
                  </div>
                  <div className="text-red-600/80 mt-1">
                    这些音轨不会进入本次发布 → App 端听不到这些点位。建议先重跑 TTS。
                  </div>
                  <details className="mt-1">
                    <summary className="cursor-pointer text-red-700">查看清单</summary>
                    <ul className="mt-1 max-h-32 overflow-y-auto space-y-0.5 pl-3">
                      {preview.staleAudios.slice(0, 30).map((a, i) => (
                        <li key={i} className="text-red-700/90">
                          · {a.sceneName} <span className="text-red-500/70">({a.part}/{a.variant}/{a.voice}/{a.lang})</span>
                        </li>
                      ))}
                      {preview.staleAudios.length > 30 && (
                        <li className="text-red-500/60">...另 {preview.staleAudios.length - 30} 条</li>
                      )}
                    </ul>
                  </details>
                </div>
              )}

              {/* pending / failed */}
              {(preview.pendingAudios.length > 0 || preview.failedAudios.length > 0) && (
                <div className="bg-amber-50 border border-amber-200 rounded p-2">
                  <div className="font-semibold text-amber-800">
                    {preview.pendingAudios.length > 0 && (
                      <>还有 {preview.pendingAudios.length} 条正在生成 </>
                    )}
                    {preview.failedAudios.length > 0 && (
                      <>{preview.pendingAudios.length > 0 ? "· " : ""}{preview.failedAudios.length} 条失败</>
                    )}
                  </div>
                  <div className="text-amber-700/80 mt-0.5">
                    这些音轨也不会进入本次发布,建议等生成完成 / 重跑失败的再发。
                  </div>
                </div>
              )}

              {preview.clean && preview.newScenes.length === 0
                && preview.scriptChangedScenes.length === 0 && preview.mediaChangedScenes.length === 0 && (
                <div className="text-gray-500">对比上次发布,内容没有变化。仍然可以发布(会创建同内容的新版本号)。</div>
              )}
            </div>
          )}
        </div>

        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="block text-xs text-gray-600 mb-1">
              版本名 <span className="text-gray-400">(格式 V&lt;主&gt;.&lt;次&gt;,默认次版本 +1;要升大版本请手动改)</span>
            </label>
            <input
              value={versionName} onChange={(e) => setVersionName(e.target.value)}
              placeholder={defaultVersionName}
              className="w-full px-3 py-2 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={mandatory} onChange={(e) => setMandatory(e.target.checked)}
              className="w-4 h-4 accent-teal-600"
            />
            强制更新(下次启动用户必须更新才能继续使用 — 谨慎勾选)
          </label>
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="flex-1 px-3 py-2 border border-gray-200 text-gray-700 rounded text-sm hover:bg-gray-50">取消</button>
            <button type="submit" disabled={busy} className="flex-1 px-3 py-2 bg-teal-600 text-white rounded text-sm font-medium hover:bg-teal-700 disabled:opacity-60">
              {busy ? "发布中…" : "确认发布"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function PackReleasesPage() {
  const [list, setList] = useState<PackRelease[]>([]);
  const [loading, setLoading] = useState(true);
  const [showPublish, setShowPublish] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const data = await apiGet<PackRelease[]>("/admin/pack-releases");
      setList(data);
    } catch (e) {
      toast(e instanceof Error ? e.message : "加载失败", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void reload(); }, []);

  const nextCode = list.length === 0 ? 1 : list[0].versionCode + 1;
  const latest = list[0];
  const defaultName = nextVersionName(latest?.versionName);

  const del = async (r: PackRelease) => {
    if (!(await confirmDialog({
      title: `删除资源包版本 ${r.versionName}?`,
      message: "删除后已下过的设备不受影响(本地文件还在);只是后续设备拉不到这个版本的清单。如果这是最新版本,会让 300 台手机退回到上一版的提示状态。",
      confirmText: "删除",
      danger: true,
    }))) return;
    try {
      await apiSend("DELETE", `/admin/pack-releases/${r.id}`);
      toast("已删除", "success");
      await reload();
    } catch (e) {
      toast(e instanceof Error ? e.message : "删除失败", "error");
    }
  };

  return (
    <div className="p-6 space-y-6 max-w-5xl mx-auto">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">资源包发布</h1>
          <p className="text-sm text-gray-500 mt-1">
            发布一次 = 把"当前 admin 内容快照"凝固成一个版本 → 推送给 300 台手机增量更新。<br />
            没发布前 admin 的临时改动 <strong>不会</strong> 打扰用户。
          </p>
        </div>
        <button
          onClick={() => setShowPublish(true)}
          className="px-4 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-semibold hover:bg-teal-700 active:scale-95 transition-transform"
        >
          <i className="ri-rocket-2-line mr-1.5" />发布新版本
        </button>
      </div>

      {latest && (
        <div className="bg-teal-50 border border-teal-200 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <i className="ri-checkbox-circle-fill text-teal-600" />
            <span className="text-sm font-semibold text-teal-700">当前生效版本: {latest.versionName}</span>
            {latest.mandatory && <span className="px-1.5 py-0.5 bg-red-100 text-red-700 text-[10px] font-bold rounded">强制更新</span>}
          </div>
          <div className="text-xs text-teal-700/80">
            共 {latest.totalCount} 个媒体文件 · {fmtBytes(latest.totalBytes)} · 发布于 {latest.createdAt}
          </div>
          {latest.changelog && (
            <div className="mt-2 text-xs text-gray-700 whitespace-pre-wrap">{latest.changelog}</div>
          )}
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200">
        <div className="px-4 py-3 border-b border-gray-100">
          <h2 className="text-sm font-semibold text-gray-900">历史版本</h2>
        </div>
        {loading ? (
          <div className="px-4 py-8 text-center text-sm text-gray-400">加载中…</div>
        ) : list.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-gray-400">还没有发布过资源包,点右上角"发布新版本"开始</div>
        ) : (
          <div className="divide-y divide-gray-100">
            {list.map((r) => (
              <div key={r.id} className="px-4 py-3 flex items-start gap-3">
                <div className="w-12 h-12 rounded-lg bg-teal-50 flex items-center justify-center shrink-0">
                  <i className="ri-archive-2-line text-teal-600 text-lg" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-gray-900">{r.versionName}</span>
                    {r.mandatory && <span className="px-1.5 py-0.5 bg-red-100 text-red-700 text-[10px] font-bold rounded">强制</span>}
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {r.totalCount} 个文件 · {fmtBytes(r.totalBytes)} · {r.createdAt}
                  </div>
                  {r.changelog && (
                    <div className="text-xs text-gray-700 mt-1 whitespace-pre-wrap">{r.changelog}</div>
                  )}
                </div>
                <button
                  onClick={() => del(r)}
                  className="px-2 py-1 border border-red-200 text-red-600 rounded text-xs hover:bg-red-50 shrink-0"
                >
                  删除
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <PublishModal
        open={showPublish}
        onClose={() => setShowPublish(false)}
        onPublished={reload}
        nextCodeHint={nextCode}
        defaultVersionName={defaultName}
      />
    </div>
  );
}
